<script setup lang="ts">
import { computed } from 'vue';
import { TickerType } from '@pkg/sanity-utils/constants/direction';
import { IMAGE_FIT } from '@pkg/sanity-utils/constants/imageFit';
import PartnerLogo from '../PartnerLogo/PartnerLogo.vue';
import { baseTickerVariants } from './variants';

export interface TickerProps {
  items: string[];
  size?: NonNullable<Parameters<typeof baseTickerVariants>[0]>['size'];
  reversed?: boolean;
  logos?: any[];
  tickerType?: TickerType;
}

const props = withDefaults(defineProps<TickerProps>(), {
  size: 'large',
  reversed: false,
  logos: undefined,
  tickerType: undefined,
});

const isLogoTicker = computed(() => props.tickerType === TickerType.LOGO_TICKER);
const hasLogos = computed(() => isLogoTicker.value && props.logos && props.logos.length > 0);
const hasItems = computed(() => props.items && props.items.length > 0);

const speed = computed(() => {
  if (hasLogos.value) {
    return calculateSpeed(props.logos!.length, 'logo');
  } else if (hasItems.value) {
    return calculateSpeed(props.items.length, 'text');
  }
  return 5000;
});

function calculateSpeed(itemCount: number, type: 'logo' | 'text'): number {
  const amountOfSpaces = itemCount - 1;
  const speedPerUnit = props.size === 'small' ? 200 : 350;

  if (type === 'text') {
    const amountOfCharacters = props.items.reduce((acc, item) => acc + item.length, 0);
    return (amountOfCharacters + (amountOfSpaces * 2.5)) * speedPerUnit;
  } else {
    return (itemCount + (amountOfSpaces * 11.5)) * speedPerUnit;
  }
}

const animationStyles = computed(() => ({
  '--c-ticker-animation-duration': `${speed.value}ms`,
}));

const COPIES = 2;
const LOGO_WIDTH = 128;
const LOGO_HEIGHT = 72;

const optimizedLogoData = computed(() => {
  if (!hasLogos.value) {
    return [];
  }

  return props.logos!.map((logo) => {
    const hasValidLogo = logo?.partnerLogo?.asset?._ref;
    const altText = logo?.partnerName || logo?.name || 'Partner logo';

    const optimizedImage = hasValidLogo
      ? {
          ...logo.partnerLogo,
          alt: altText,
          fit: IMAGE_FIT.CONTAIN,
        }
      : null;

    const imageOptions = {
      width: LOGO_WIDTH * 2,
      height: LOGO_HEIGHT * 2,
      quality: 85,
      fit: 'max' as const,
    };

    return {
      type: 'logo' as const,
      key: logo._key,
      data: {
        ...logo,
        optimizedImage,
        imageOptions,
        altText,
        hasValidLogo,
      },
    };
  });
});

const tickerContent = computed(() => {
  if (isLogoTicker.value && hasLogos.value) {
    return optimizedLogoData.value;
  } else if (hasItems.value) {
    return props.items.map(item => ({
      type: 'text' as const,
      key: item,
      data: item,
    }));
  }
  return [];
});
</script>

<template>
  <div>
    <div
      v-if="size === 'large' && tickerType === TickerType.TEXT_TICKER"
      :class="baseTickerVariants({ size, reversed: !reversed, mobile: true })"
      :style="animationStyles"
    >
      <div
        v-for="index in COPIES"
        :key="`mobile-${index}`"
        class="c-ticker__content flex flex-row flex-nowrap items-center"
      >
        <div
          v-for="item in tickerContent"
          :key="item.key"
          class="c-ticker__item"
        >
          <span>{{ item.data }}</span>
        </div>
      </div>
    </div>

    <div
      :class="baseTickerVariants({ size, reversed })"
      :style="animationStyles"
    >
      <div
        v-for="index in COPIES"
        :key="`desktop-${index}`"
        class="c-ticker__content flex flex-row flex-nowrap items-center"
      >
        <div
          v-for="item in tickerContent"
          :key="item.key"
          :class="['c-ticker__item', { 'c-ticker__item--logo': item.type === 'logo' }]"
        >
          <component
            :is="PartnerLogo"
            v-if="item.type === 'logo'"
            :partner-logo="item.data"
            :optimized-image="item.data.optimizedImage"
            :image-options="item.data.imageOptions"
            :alt-text="item.data.altText"
            :has-valid-logo="item.data.hasValidLogo"
          />
          <span v-else-if="item.type === 'text'">{{ item.data }}</span>
          <span v-else class="text-gray-400">No content available</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

.c-ticker {
  --animation-direction: normal;

  &--large {
    --gap: 3.5rem;
    color: var(--ticker-large-color);
    @mixin h1;
  }

  &--small {
    --gap: 2.5rem;
    color: var(--ticker-small-color);
    @mixin h3;
  }

  &--reversed {
    --animation-direction: reverse;
  }

  &--mobile {
    margin-bottom: 0.5rem;

    @screen tablet-wide {
      display: none !important;
    }
  }

  gap: var(--gap);
  max-width: 100vw;
  display: flex;
  overflow: visible;
  transform: translateZ(0);
  backface-visibility: hidden;

  @keyframes ticker {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(calc(-100% - var(--gap)), 0, 0);
    }
  }

  &__content {
    animation-name: ticker;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-direction: var(--animation-direction);
    animation-duration: var(--c-ticker-animation-duration);
    gap: var(--gap);
    display: flex;
    flex-shrink: 0;
    width: max-content;
    min-width: 100%;
    justify-content: space-around;
    will-change: transform;
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);
    perspective: 1000px;
  }

  &__item {
    white-space: nowrap;
    flex-wrap: nowrap;
    text-transform: uppercase;
    position: relative;
    display: flex;
    align-items: center;
    transform: translateZ(0);

    &--logo {
      text-transform: none;
      white-space: normal;
      flex-shrink: 0;
      transform: translateZ(0);
    }
  }
}
</style>
